/**
 * PlatformSelection.tsx
 * Manages the selection of distribution platforms for the release.
 * Includes a select all option and individual platform toggles.
 * Filters DSPs based on user's company's available DSPs.
 */

import React, { useState, useEffect } from 'react';
import { PlatformSelectionProps } from '../../types/components';
import { SelectedDSPs } from '../../types/form';
import { userApi } from '../../../repositories/user';

interface Platform {
  id: keyof SelectedDSPs;
  label: string;
  apiName: string; // The name as stored in the database
}

interface UserProfile {
  Label?: {
    availableDSPs?: string[];
  };
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({ 
  selectedDSPs, 
  setSelectedDSPs 
}) => {
  const [availableDSPs, setAvailableDSPs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // All possible platforms with their API names
  const allPlatforms: Platform[] = [
    { id: 'spotify', label: 'Spotify', apiName: 'Spotify' },
    { id: 'amazon', label: 'Amazon', apiName: 'Amazon Music' },
    { id: 'deezer', label: 'Deezer', apiName: 'Deezer for Creators' },
    { id: 'audiomack', label: 'Audiomack', apiName: 'Audiomack' },
    { id: 'apple', label: 'Apple', apiName: 'Apple Music' },
    { id: 'TIDAL', label: 'TIDAL', apiName: 'TIDAL' },
    { id: 'youtube', label: 'YouTube', apiName: 'YouTube' },
    { id: 'email', label: 'Email', apiName: 'Email' },
    { id: 'soundcloud', label: 'SoundCloud', apiName: 'SoundCloud' },
  ];

  // Fetch user's company DSP access
  useEffect(() => {
    const fetchUserDSPs = async () => {
      try {
        setLoading(true);
        const response = await userApi.getProfile();
        if (response?.data?.success) {
          const userData: UserProfile = response.data.data;
          const companyDSPs = userData.Label?.availableDSPs || [];
          setAvailableDSPs(companyDSPs);
          
          // If no DSPs are available, show error
          if (companyDSPs.length === 0) {
            setError('No DSPs are available for your company. Please contact your administrator.');
          }
        } else {
          setError('Failed to fetch company DSP access');
        }
      } catch (error) {
        console.error('Error fetching user DSPs:', error);
        setError('Failed to load DSP access. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDSPs();
  }, []);

  // Filter platforms based on available DSPs
  const availablePlatforms = allPlatforms.filter(platform => {
    // Email should always be available
    if (platform.id === 'email') return true;
    
    // Check if the platform is in the user's company's available DSPs
    return availableDSPs.includes(platform.apiName);
  });

  const handleCheckboxChange = (dspId: keyof SelectedDSPs) => {
    setSelectedDSPs(prev => ({
      ...prev,
      [dspId]: !prev[dspId]
    }));
  };

  const handleSelectAll = () => {
    const areAllSelected = availablePlatforms.every(platform => selectedDSPs[platform.id]);
    const newValue = !areAllSelected;
    
    const updatedSelections = { ...selectedDSPs };
    availablePlatforms.forEach(platform => {
      updatedSelections[platform.id] = newValue;
    });
    
    setSelectedDSPs(updatedSelections);
  };

  if (loading) {
    return (
      <section className="p-8 bg-white rounded-lg shadow-md">
        <h4 className="mb-6 text-2xl font-semibold text-gray-900">Where to pitch?</h4>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
            <p className="text-gray-600">Loading available platforms...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="p-8 bg-white rounded-lg shadow-md">
        <h4 className="mb-6 text-2xl font-semibold text-gray-900">Where to pitch?</h4>
        <div className="p-4 text-center border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-600">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="p-8 bg-white rounded-lg shadow-md">
      {/* Section Title */}
      <h4 className="mb-6 text-2xl font-semibold text-gray-900">Where to pitch?</h4>
  
      {/* Platform Checkboxes */}
      <div className="grid grid-cols-1 gap-5 mb-6 sm:grid-cols-2 md:grid-cols-3">
        {availablePlatforms.map((platform) => (
          <label
            key={platform.id}
            className="flex items-center p-4 space-x-3 transition bg-gray-100 rounded-lg shadow-sm cursor-pointer hover:bg-gray-200"
          >
            <input
              type="checkbox"
              checked={selectedDSPs[platform.id]}
              onChange={() => handleCheckboxChange(platform.id)}
              className="w-6 h-6 text-blue-600 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-lg font-medium text-gray-800">{platform.label}</span>
          </label>
        ))}
      </div>
  
      {/* Select All Button */}
      {availablePlatforms.length > 0 && (
        <button
          type="button"
          onClick={handleSelectAll}
          className="w-full px-6 py-3 text-lg font-medium text-white transition bg-blue-600 rounded-lg sm:w-auto hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Select All
        </button>
      )}
    </section>
  );
  
};

export default PlatformSelection;